import { Box, Button, Text, useConfirmationDialog } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import AttachmentBox from 'modules/complaints/components/AttachmentBox'
import complaints from 'modules/complaints/store/collections/complaints'

import { useComplaint } from '../complaintContext'

const useOptions = () => {
  const { complaint, complaintMessages, complaintMessageFiles } = useComplaint()

  if (!complaint) throw new Error('Complaint not found')

  const message = complaintMessages.at(0)

  const files = complaintMessageFiles.filter({
    message_id: message?.attributes.id,
  })

  const filesValue =
    files.length === 0 ? (
      '-'
    ) : (
      <Box gap="s4">
        {files.map((attachment, index) => (
          <AttachmentBox key={index} name={attachment.name} path={attachment.path} />
        ))}
      </Box>
    )

  const options = [
    {
      label: i18n.t('complaints.overview.created'),
      value: complaint.createdAt.format('ll'),
    },
    {
      label: i18n.t('complaints.overview.due_date'),
      value: complaint.acknowledgeDueAt?.format('ll'),
    },
    {
      label: i18n.t('complaints.overview.description'),
      value: message?.text || '-',
    },
    {
      label: i18n.t('complaints.overview.files'),
      value: filesValue,
    },
  ]

  if (complaint.complainerName) {
    options.unshift(
      {
        label: i18n.t('complaints.overview.complainer_name'),
        value: complaint.complainerName,
      },
      {
        label: i18n.t('complaints.overview.complainer_email'),
        value: complaint.complainerEmail ?? '', // TODO: Review this default.
      }
    )
  }

  options.unshift({
    label: i18n.t('complaints.overview.relationship'),
    value: complaint.complainerRelationship || '',
  })

  return options
}

interface Props {
  onDeleteComplaint: () => void
}

const Overview = ({ onDeleteComplaint }: Props) => {
  const options = useOptions()
  const { openDialog } = useConfirmationDialog()
  const { complaint } = useComplaint()

  if (!complaint) {
    throw new Error('Complaint not found')
  }

  const onDelete = async () => {
    const { success } = await asyncAction(() => complaints.delete(complaint), {
      successMessage: i18n.t('common.saved_successfully'),
      loadingMessage: i18n.t('common.deleting'),
    })

    if (success) {
      onDeleteComplaint()
    }
  }

  return (
    <Box gap="s40">
      <Box gap="s32" justifyContent="flexStart">
        {options.map((option) => (
          <Box key={option.label} gap="s8">
            <Text.Normal size="200" color="grey800" weight="medium">
              {option.label}
            </Text.Normal>
            {typeof option.value !== 'string' ? (
              option.value
            ) : (
              <Text.Normal size="300" color="grey1000" weight="medium" overflowWrap>
                {option.value}
              </Text.Normal>
            )}
          </Box>
        ))}

        <Button
          type="secondary"
          onClick={() =>
            openDialog({
              illustration: 'delete',
              title: i18n.t('complaints.soft_delete_modal.title'),
              description: i18n.t('complaints.soft_delete_modal.description'),
              cancelLabel: i18n.t('complaints.soft_delete_modal.cancel'),
              confirmLabel: i18n.t('complaints.soft_delete_modal.delete'),
              onConfirm: onDelete,
            })
          }
        >
          {i18n.t('complaints.overview.delete_complaint')}
        </Button>
      </Box>
    </Box>
  )
}

export default observer(Overview)
